import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  createNgModule,
  Inject,
  Injector,
  Input,
  NgModuleRef,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { EventsService } from '@app/core/services';

const DESKTOP_MAP_LOAD_DELAY_MS = 1_500;

@Component({
  selector: 'app-lazy-side-map',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-container #container></ng-container>
    <div
      *ngIf="isPlaceholderVisible"
      class="_map-placeholder"
      [class._map-placeholder--hidden]="isPlaceholderHidden"
    ></div>
  `,
  styles: [
    `
      :host {
        display: block;
        position: relative;
        height: var(--innerHeight, 100vh);
        min-width: 0;
      }

      ._map-placeholder {
        position: absolute;
        inset: 0;
        overflow: hidden;
        height: 100%;
        width: 100%;
        background: #dde4e2;
        opacity: 1;
        transition: opacity 280ms ease;
      }

      ._map-placeholder::before {
        content: '';
        position: absolute;
        inset: 0 auto 0 0;
        width: 55%;
        background: linear-gradient(90deg, transparent 0%, #f7f9f8 50%, transparent 100%);
        transform: translateX(-100%);
        animation: map-placeholder-pulse 1.2s ease-in-out infinite;
        will-change: transform;
      }

      ._map-placeholder--hidden {
        opacity: 0;
      }

      @keyframes map-placeholder-pulse {
        0%,
        100% {
          transform: translateX(-100%);
        }
        50% {
          transform: translateX(180%);
        }
      }
    `,
  ],
})
export class LazySideMapComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() isWlMap = false;

  @ViewChild('container', { read: ViewContainerRef, static: true })
  private readonly container: ViewContainerRef;

  public readonly isBrowser: boolean;
  public componentRef: ComponentRef<any> | null = null;
  public isPlaceholderVisible = true;
  public isPlaceholderHidden = false;

  private moduleRef: NgModuleRef<any> | null = null;
  private mobileMapSubscription: Subscription | null = null;
  private loadTimer: ReturnType<typeof setTimeout> | null = null;
  private placeholderTimer: ReturnType<typeof setTimeout> | null = null;
  private loading = false;
  private viewReady = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private readonly injector: Injector,
    private readonly eventsService: EventsService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isWlMap && this.componentRef) {
      this.componentRef.instance.isWlMap = this.isWlMap;
      this.componentRef.changeDetectorRef.markForCheck();
    }

    if (this.viewReady) {
      void this.loadMap();
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.scheduleMapLoad();
  }

  ngOnDestroy(): void {
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
    }
    if (this.placeholderTimer) {
      clearTimeout(this.placeholderTimer);
    }
    this.mobileMapSubscription?.unsubscribe();
    this.componentRef?.destroy();
    this.moduleRef?.destroy();
  }

  private scheduleMapLoad(): void {
    if (!this.isBrowser) {
      return;
    }

    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (isMobile) {
      this.mobileMapSubscription = this.eventsService.openMobileMap$.subscribe(
        value => {
          if (value) {
            void this.loadMap();
          }
        },
      );
      return;
    }

    this.loadTimer = setTimeout(() => {
      void this.loadMap();
    }, DESKTOP_MAP_LOAD_DELAY_MS);
  }

  private async loadMap(): Promise<void> {
    if (!this.isBrowser || this.componentRef || this.loading) {
      return;
    }

    this.loading = true;
    const [{ SideMapComponent }, { SideMapComponentModule }] =
      await Promise.all([
        import('./side-map.component'),
        import('./side-map.component.module'),
      ]);

    this.moduleRef = createNgModule(SideMapComponentModule, this.injector);
    this.componentRef = this.container.createComponent(SideMapComponent, {
      ngModuleRef: this.moduleRef,
    });
    this.componentRef.instance.isWlMap = this.isWlMap;
    this.componentRef.changeDetectorRef.detectChanges();
    this.hidePlaceholder();
  }

  private hidePlaceholder(): void {
    this.isPlaceholderHidden = true;
    this.placeholderTimer = setTimeout(() => {
      this.isPlaceholderVisible = false;
    }, 300);
  }
}
